import clone from 'lodash.clonedeep';
import { toImageUrl } from '../utils/format.util';
import { snapshotExists } from '../utils/snapshot.util';

export default class SalesService {
  constructor(firebase) {
    this.firebase = firebase;
  }

  async getSales(
    cardId,
    limit = 20,
    queryCursor = null,
    sortField = 'date',
    sortDirection = 'desc',
    dateFilter = null,
  ) {
    let query = this.firebase.firestore()
      .collection('cards')
      .doc(cardId)
      .collection('sales');

    if (dateFilter) {
      query = query.where('date', '<', dateFilter);
    }

    query = query.orderBy(sortField, sortDirection);
    query = query.limit(limit);

    if (queryCursor) {
      query = query.startAfter(queryCursor);
    }

    const querySnapshot = await query.get();
    const sales = [];

    querySnapshot.forEach((doc) => {
      sales.push({
        ...doc.data(),
        id: doc.id,
        date: doc.data().date.toDate(),
      });
    });

    return { data: sales, cursor: querySnapshot.docs[querySnapshot.docs.length - 1] };
  }

  async getPurgatorySalesForCard(cardId, limit = 20) {
    let query = this.firebase.firestore()
      .collection('purgatorySales')
      .where('cardId', '==', cardId);

    query = query.orderBy('date', 'desc');
    query = query.limit(limit);

    const querySnapshot = await query.get();
    const purgatorySales = [];

    querySnapshot.forEach((doc) => {
      purgatorySales.push({
        ...doc.data(),
        date: doc.data().date.toDate(),
      });
    });

    return { data: purgatorySales, cursor: querySnapshot.docs[querySnapshot.docs.length - 1] };
  }

  async addSale(cardId, sale) {
    return this.firebase.firestore()
      .collection('cards')
      .doc(cardId)
      .collection('sales')
      .doc(sale.itemId)
      .set(sale);
  }

  async deleteSale(cardId, saleId) {
    return this.firebase.firestore()
      .collection('cards')
      .doc(cardId)
      .collection('sales')
      .doc(saleId)
      .delete();
  }

  async deletePurgatorySale(itemId) {
    return this.firebase.firestore()
      .collection('purgatorySales')
      .doc(itemId)
      .delete();
  }

  async deleteArchiveSale(item) {
    if (item.platform === 'eBay') {
      await this.firebase.firestore()
        .collection('ebayDeletes')
        .doc(item.itemId)
        .set(item);
    }

    return this.firebase.firestore()
      .collection('salesArchive')
      .doc(item.itemId)
      .delete();
  }

  async findClosestSale(cardId, date, minDate, maxDate) {
    let closestSale = null;

    const closeSales = await this.firebase.firestore()
      .collection('cards')
      .doc(cardId)
      .collection('sales')
      .where('date', '>', minDate)
      .where('date', '<', maxDate)
      .get();

    const sales = [];

    closeSales.forEach((saleDoc) => {
      if (saleDoc && snapshotExists(saleDoc) && saleDoc.data() && typeof saleDoc.data().price === 'number' && saleDoc.data().date) {
        sales.push({
          ...saleDoc.data(),
          date: saleDoc.data().date.toDate(),
        });
      }
    });

    const temp = sales.map((sale) => Math.abs(date.getTime() - sale.date.getTime()));
    const index = temp.indexOf(Math.min(...temp));

    if (index > -1) {
      closestSale = sales[index];
    }

    return closestSale;
  }

  async getAllTimeHighSale(cardId) {
    let allTimeHighSale = null;

    const highestSaleQuery = await this.firebase.firestore()
      .collection('cards')
      .doc(cardId)
      .collection('sales')
      .orderBy('price', 'desc')
      .limit(1)
      .get();

    highestSaleQuery.forEach((highestSaleDoc) => {
      if (snapshotExists(highestSaleDoc) && highestSaleDoc.data()) {
        allTimeHighSale = {
          ...highestSaleDoc.data(),
          date: highestSaleDoc.data().date.toDate(),
        };
      }
    });

    return allTimeHighSale;
  }

  async getNumSalesSinceDate(cardId, date) {
    let numSales = 0;

    const salesQuery = await this.firebase.firestore()
      .collection('cards')
      .doc(cardId)
      .collection('sales')
      .where('date', '>', date)
      .get();

    if (salesQuery && salesQuery.size) {
      numSales = salesQuery.size;
    }

    return numSales;
  }

  async confirmSale(pendingSale, purgatory = false) {
    const collection = purgatory ? 'purgatorySales' : 'pendingSales';

    try {
      await this.firebase.firestore().collection(collection).doc(pendingSale.itemId).delete();
    } catch (e) {
      return;
    }

    const pendingSaleToSave = clone(pendingSale);

    if (pendingSaleToSave.platform === 'eBay') {
      pendingSaleToSave.itemId = pendingSaleToSave.itemId.startsWith(pendingSaleToSave.platform.toLowerCase()) ? pendingSaleToSave.itemId : `${pendingSaleToSave.platform.toLowerCase()}-${pendingSaleToSave.itemId}`;
    }

    delete pendingSaleToSave.currentValue;
    delete pendingSaleToSave.marketValue;
    delete pendingSaleToSave.purgatory;
    delete pendingSaleToSave.eBayQuery;
    delete pendingSaleToSave.newestSaleDate;
    delete pendingSaleToSave.scrapeSuccess;
    delete pendingSaleToSave.scrapeFailed;
    delete pendingSaleToSave.permanent;
    delete pendingSaleToSave.displayDate;
    delete pendingSaleToSave.employeeNotes;
    delete pendingSaleToSave.needsReview;
    delete pendingSaleToSave.sendToPending;
    delete pendingSaleToSave.log;
    delete pendingSaleToSave.status;
    delete pendingSaleToSave.verifiedCert;
    delete pendingSaleToSave.switchedCardIdFromCert;
    delete pendingSaleToSave.noMatchingCert;
    delete pendingSaleToSave.noCert;
    delete pendingSaleToSave.previousCardId;

    if (pendingSaleToSave.cardId) {
      await this.firebase.firestore()
        .collection('cards')
        .doc(pendingSaleToSave.cardId)
        .collection('sales')
        .doc(pendingSaleToSave.itemId)
        .set(pendingSaleToSave);
    }
  }

  async deletePendingSale(pendingSale) {
    await this.firebase.firestore()
      .collection('pendingSales')
      .doc(pendingSale.itemId)
      .delete();
  }

  async movePendingSaleToPurgatory(itemId) {
    const pendingSaleDoc = await this.firebase.firestore().collection('pendingSales').doc(itemId).get();

    if (snapshotExists(pendingSaleDoc) && pendingSaleDoc.data()) {
      await this.firebase.firestore()
        .collection('purgatorySales')
        .doc(itemId)
        .set(pendingSaleDoc.data());

      await this.firebase.firestore()
        .collection('pendingSales')
        .doc(itemId)
        .delete();
    }
  }

  async moveSaleToPurgatory(cardId, sale) {
    const saleDoc = await this.firebase.firestore()
      .collection('cards')
      .doc(cardId)
      .collection('sales')
      .doc(sale.id)
      .get();

    if (snapshotExists(saleDoc) && saleDoc.data() && sale.itemId) {
      await this.firebase.firestore()
        .collection('purgatorySales')
        .doc(sale.itemId)
        .set(sale);

      await this.firebase.firestore()
        .collection('cards')
        .doc(cardId)
        .collection('sales')
        .doc(sale.id)
        .delete();
    }
  }

  async updatePendingSale(pendingSale) {
    return this.firebase.firestore()
      .collection('pendingSales')
      .doc(pendingSale.itemId)
      .update(pendingSale);
  }

  async createOrUpdateSalesHistorySale(sale) {
    const itemId = sale.itemId || `${sale.platform.toLowerCase()}-${Date.now().toString()}`;
    const update = {
      title: sale.title,
      price: sale.price,
      date: sale.date,
      platform: sale.platform,
      listingType: sale.listingType,
      url: sale.url,
      notes: sale.notes,
      image: typeof sale.image === 'string' ? sale.image : '',
      memberVerified: sale.memberVerified || false,
      ignoreIndex: false,
      sendToPending: false,
    };

    if (sale.image && (sale.image.name || sale.image.size)) {
      const path = `sales/${itemId}`;
      const imageRef = this.firebase.storage().ref().child(path);

      await imageRef.put(sale.image);

      update.image = toImageUrl(path);
    }

    if (sale.itemId) {
      await this.firebase.firestore()
        .collection('salesArchive')
        .doc(sale.itemId)
        .update(update);
    } else {
      update.itemId = itemId;

      await this.firebase.firestore()
        .collection('salesArchive')
        .doc(itemId)
        .set(update);
    }

    return itemId;
  }

  async updateSalesHistorySale(sale) {
    return this.firebase.firestore()
      .collection('salesArchive')
      .doc(sale.itemId)
      .update(sale);
  }

  async flagForHeadlines(sale) {
    return this.firebase.firestore()
      .collection('possibleHeadlines')
      .doc(sale.itemId)
      .set(sale);
  }

  async getPossibleHeadlines() {
    const querySnapshot = await this.firebase.firestore()
      .collection('possibleHeadlines')
      .orderBy('price', 'desc')
      .get();

    const possibleHeadlines = [];

    querySnapshot.forEach((doc) => {
      possibleHeadlines.push({
        ...doc.data(),
        date: doc.data().date.toDate(),
        id: doc.id,
      });
    });

    return possibleHeadlines;
  }

  async deletePossibleHeadline(id) {
    return this.firebase.firestore()
      .collection('possibleHeadlines')
      .doc(id)
      .delete();
  }

  async submitEbayItemId(ebayItemId, uid) {
    return this.firebase.firestore().collection('ebaySoldBatch').add({
      itemIds: [ebayItemId],
      requestUid: uid,
    });
  }
}
